import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { usePartnersDownloadLeadsCsv, usePartnersReadCampaignLeads } from '@/api/hooks';
import { type PartnerCampaign } from '@/api/routes/partners/partners.campaigns.api';
import Button from '@/components/Button';
import LoadErrorState from '@/components/LoadErrorState';
import { IconDownload } from '@/components/icon/tabler/IconDownload';
import { formatPhone } from '@/domain/offers/utils';
import {
  LEADS_PAGE_SIZE,
  formatLeadDate,
  leadsCsvFilename,
  placementSlotLabel,
} from '@/domain/results/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  campaign: PartnerCampaign;
}

/**
 * Contacts of users who accepted to share them, with the exact sentence each one accepted.
 * The only identity the partner ever sees; reading them marks them delivered.
 */
const CampaignLeadsSection = ({ campaign }: Props) => {
  const t = useTranslate('domain.results.components.CampaignLeadsSection');
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, refetch } = usePartnersReadCampaignLeads(campaign.id, {
    offset: page * LEADS_PAGE_SIZE,
    limit: LEADS_PAGE_SIZE,
  });
  const { mutate: download, isPending: isDownloading } = usePartnersDownloadLeadsCsv();

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack gap={1} sx={{ p: 2.5 }} aria-busy="true">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} variant="text" />
          ))}
        </Stack>
      );
    }
    if (isError || !data) {
      return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
    }
    if (!data.items.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
          {t('empty')}
        </Typography>
      );
    }
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.name')}</TableCell>
                <TableCell>{t('columns.email')}</TableCell>
                <TableCell>{t('columns.phone')}</TableCell>
                <TableCell>{t('columns.consentedAt')}</TableCell>
                <TableCell>{t('columns.slot')}</TableCell>
                <TableCell>{t('columns.consentText')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>{lead.name}</TableCell>
                  <TableCell>{lead.email ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {lead.phone ? formatPhone(lead.phone) : '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatLeadDate(lead.consentedAt)}
                  </TableCell>
                  <TableCell>{placementSlotLabel[lead.slot]}</TableCell>
                  <TableCell>
                    <Tooltip title={lead.consentText}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        {lead.consentText}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.count > LEADS_PAGE_SIZE ? (
          <TablePagination
            component="div"
            count={data.count}
            page={page}
            rowsPerPage={LEADS_PAGE_SIZE}
            rowsPerPageOptions={[]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            labelDisplayedRows={({ from, to, count }) => t('pagination', { from, to, count })}
            getItemAriaLabel={(type) => t(`paginationAria.${type}`)}
          />
        ) : null}
      </>
    );
  };

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        sx={{ px: 2.5, pt: 2.5, pb: 1 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {t('title', { count: data?.count ?? 0 })}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<IconDownload />}
          loading={isDownloading}
          disabled={!data?.count}
          onClick={() => download({ id: campaign.id, filename: leadsCsvFilename(campaign) })}
        >
          {t('csv')}
        </Button>
      </Stack>
      {renderBody()}
      <Alert severity="info" sx={sx.privacy}>
        {t('privacy')}
      </Alert>
    </Paper>
  );
};

export default CampaignLeadsSection;

const sx = {
  paper: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
  privacy: { m: 2.5, mt: 1 },
};
